/**
 * Expanded detail for an `operational_digest` feed root. Renders the day's
 * bookings for one operational dimension (stuffing / cutoff / departing /
 * arriving / transshipment / delayed), each with a booking deep-link, lane,
 * handling agent / shipper, and status flag chips (on-board / ETD diverged /
 * rolled / delayed). Surfaces any CTA chips (e.g. "Confirm on board" for
 * yesterday's departures) and a "Show more" link into the bookings table
 * filtered + grouped by the relevant operational date.
 *
 * The digest is a single event per (dimension, day, portfolio owner), so it
 * renders inline under the normal `FeedEventRow` rather than as a collapsed
 * group. Already-emitted cards may omit agent / shipper; those are hydrated
 * from the booking detail so operators get a quick party ref without waiting
 * for a digest re-emit.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, MailPlus } from "lucide-react";

import { cn } from "@/lib/utils";
import {
  SensitiveInlineText,
} from "@/components/common/Sensitive";
import { FeedBookingDrillPartyLine } from "./FeedBookingDrillParties";
import { api } from "@/services/api";
import type { ApiBooking } from "@/types/api";
import type {
  OperationalDigestBooking,
  OperationalDigestPayload,
} from "@/types/feed";
import { bookingHref, operationalDigestShowMoreHref } from "./feedLinks";

/** Whether a payload is an `operational_digest` root. */
export function isOperationalDigestPayload(
  payload: unknown,
): payload is OperationalDigestPayload {
  if (!payload || typeof payload !== "object") return false;
  const p = payload as Record<string, unknown>;
  return p.kind === "operational_digest" && Array.isArray(p.bookings);
}

function firstText(
  ...values: Array<string | null | undefined>
): string | undefined {
  for (const value of values) {
    const trimmed = value?.trim();
    if (trimmed) return trimmed;
  }
  return undefined;
}

function etaDelayDaysFromBooking(booking: ApiBooking | null): number | null {
  const raw = booking?.extra_data?.operational_delay;
  if (!raw || typeof raw !== "object") return null;
  const days = (raw as Record<string, unknown>).eta_delay_days;
  return typeof days === "number" ? days : null;
}

/** Merge live booking fields onto a digest row (parties + stuffed ≠ on board). */
export function mergeOperationalDigestBooking(
  row: OperationalDigestBooking,
  booking: ApiBooking | null,
): OperationalDigestBooking {
  const state = firstText(booking?.state, row.state);
  const flags = { ...(row.flags ?? {}) };
  if (state === "stuffed") flags.on_board = false;
  const tags = booking?.tags ?? [];
  if (tags.includes("ROLLED")) flags.rolled = true;
  if (tags.includes("DELAYED")) flags.delayed = true;
  const etaDays = etaDelayDaysFromBooking(booking);
  if (etaDays != null && etaDays > 0) {
    flags.eta_delayed = true;
    flags.delayed = true;
  }
  return {
    ...row,
    state,
    agent: firstText(row.agent, booking?.agent),
    shipper: firstText(row.shipper, booking?.shipper_name, booking?.shipper),
    consignee: firstText(row.consignee, booking?.consignee),
    flags,
  };
}

function digestNeedsHydrate(
  row: OperationalDigestBooking,
  dimension: string,
): boolean {
  if (dimension === "delayed") return true;
  return !row.agent || !row.shipper || !row.consignee;
}

function useHydratedDigestBookings(
  bookings: OperationalDigestBooking[],
  dimension: string,
) {
  const [rows, setRows] = useState<OperationalDigestBooking[]>(bookings);
  const hydrateKey = bookings
    .map(
      (row) =>
        `${row.source_id}:${row.agent ?? ""}:${row.shipper ?? ""}:${row.consignee ?? ""}`,
    )
    .join("|");

  useEffect(() => {
    let cancelled = false;
    setRows(bookings);
    void Promise.all(
      bookings.map(async (row) => {
        if (!digestNeedsHydrate(row, dimension)) return row;
        const booking = await api.getBooking(row.source_id).catch(() => null);
        return mergeOperationalDigestBooking(row, booking);
      }),
    ).then((next) => {
      if (!cancelled) setRows(next);
    });
    return () => {
      cancelled = true;
    };
    // payload.bookings is a new array each render; key on identity + parties.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hydrateKey, dimension]);

  return rows;
}

/** Short, tone-coded chips for the truthy flags on a digest booking. */
function flagChips(booking: OperationalDigestBooking) {
  const flags = booking.flags ?? {};
  const chips: { key: string; label: string; className: string }[] = [];
  if (flags.on_board) {
    chips.push({
      key: "on_board",
      label: "On board",
      className: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-300",
    });
  }
  if (flags.etd_diverged) {
    chips.push({
      key: "etd_diverged",
      label: "ETD moved",
      className: "bg-amber-500/15 text-amber-600 dark:text-amber-300",
    });
  }
  if (flags.rolled) {
    chips.push({
      key: "rolled",
      label: "Rolled",
      className: "bg-rose-500/15 text-rose-600 dark:text-rose-300",
    });
  }
  if (flags.delayed) {
    chips.push({
      key: "delayed",
      label: "Delayed",
      className: "bg-orange-500/15 text-orange-600 dark:text-orange-300",
    });
  }
  if (flags.eta_delayed) {
    chips.push({
      key: "eta_delayed",
      label: "ETA delayed",
      className: "bg-orange-500/15 text-orange-600 dark:text-orange-300",
    });
  }
  return chips;
}

function DigestBookingRow({
  booking,
}: {
  booking: OperationalDigestBooking;
}) {
  const chips = flagChips(booking);
  const hasParties = Boolean(
    booking.agent || booking.shipper || booking.consignee,
  );
  return (
    <div className="rounded-lg bg-white/5 px-2.5 py-2">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <Link
            to={bookingHref(booking.source_id)}
            className="inline-flex max-w-full items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-medium text-primary transition-colors hover:bg-primary/20"
          >
            #<SensitiveInlineText text={booking.source_id} />
            <ArrowUpRight className="size-2.5 shrink-0" />
          </Link>
          {hasParties && (
            <FeedBookingDrillPartyLine
              variant="summary"
              omitAgent
              agent={booking.agent}
              shipper={booking.shipper}
              consignee={booking.consignee}
              className="mt-1"
            />
          )}
        </div>
        {chips.length > 0 && (
          <div className="flex max-w-[45%] shrink-0 flex-wrap justify-end gap-1">
            {chips.map((chip) => (
              <span
                key={chip.key}
                className={cn(
                  "rounded-full px-1.5 py-0.5 text-[9px] font-medium",
                  chip.className,
                )}
              >
                {chip.label}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export function FeedOperationalDigestDetail({
  payload,
}: {
  payload: OperationalDigestPayload;
}) {
  const bookings = useHydratedDigestBookings(
    payload.bookings,
    payload.dimension,
  );

  const total = payload.booking_count;
  const showMoreHref = operationalDigestShowMoreHref(
    payload.show_more,
    payload.scope,
    payload.bookings.map((b) => b.source_id),
  );

  return (
    <div className="feed-row-detail-body feed-row-detail-body--loose">
      <p className="feed-row-detail-body__heading">
        {total} booking{total === 1 ? "" : "s"} {payload.relative}
      </p>

      {payload.ctas.length > 0 && (
        <div className="flex flex-wrap gap-1 px-0.5">
          {payload.ctas.map((cta, i) => {
            const to = cta.source_id ? bookingHref(cta.source_id) : showMoreHref;
            return (
              <Link
                key={`${cta.action}:${cta.source_id ?? i}`}
                to={to}
                className="inline-flex items-center gap-1 rounded-full bg-sky-500/15 px-2 py-0.5 text-[10px] font-medium text-sky-600 transition-colors hover:bg-sky-500/25 dark:text-sky-300"
              >
                <MailPlus className="size-2.5" />
                {cta.label}
                {cta.source_id ? (
                  <>
                    {" "}
                    #<SensitiveInlineText text={cta.source_id} />
                  </>
                ) : null}
              </Link>
            );
          })}
        </div>
      )}

      <div className="flex flex-col gap-1.5">
        {bookings.map((booking) => (
          <DigestBookingRow key={booking.source_id} booking={booking} />
        ))}
      </div>
    </div>
  );
}
